import { useRef } from "react";
import {
  Box,
  Button,
  List,
  ListItem,
  useColorModeValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useMediaQuery,
  Heading,
  IconButton,
} from "@chakra-ui/react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context";
import ToggleColor from "./ToggleColor";
import { CollecticonHamburgerMenu } from "@devseed-ui/collecticons-chakra";

type MenuLinkProps = {
  href: string;
  children: React.ReactNode;
};

function MenuLink({ children, href }: MenuLinkProps) {
  const { pathname } = useLocation();
  const [isMediumDown] = useMediaQuery("(max-width: 800px)");
  const isActive = pathname.startsWith(href);

  return (
    <ListItem
      borderBottom="2px solid"
      borderColor={isActive ? "primary.500" : "transparent"}
      fontSize="0.75rem"
      letterSpacing="0.06rem"
      px={isMediumDown ? "" : "2"}
      fontFamily="mono"
      textTransform="uppercase"
      sx={{
        "& > *": {
          verticalAlign: "middle",
        }
      }}
    >
      <Link to={href}>{children}</Link>
    </ListItem>
  );
}

type NavMenuProps = {
  isAuthenticated: boolean;
  isMediumDown: boolean;
  handleLogout: () => void;
};

function NavMenuList({ isAuthenticated, handleLogout, isMediumDown }: NavMenuProps) {
  return(
    <List display="flex" flexDirection={isMediumDown ? "column" : "row"} gap={isMediumDown ? "4" : "10"}>
      {isAuthenticated && (
        <MenuLink href="/scenes">Scenes</MenuLink>
      )}
      <MenuLink href="/explore">Explore</MenuLink>
      <MenuLink href="https://docs.whalesai.org">User Guide</MenuLink>
      {isAuthenticated ? (
        <Button
          variant="link"
          onClick={handleLogout}
          fontFamily="mono"
          fontWeight="normal"
          fontSize="0.75rem"
          letterSpacing="0.06rem"
          textTransform="uppercase"
          justifyContent="flex-start"
          py="0"
        >
    Logout
        </Button>
      ) : (
        <MenuLink href="/login">Login</MenuLink>
      )}
    </List>);
}

function GlobalPageHeader() {
  const bg = useColorModeValue("white", "base.500");
  const linkColor = useColorModeValue("primary.700", "primary.400");
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const [isMediumDown] = useMediaQuery("(max-width: 800px)");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const btnRef = useRef<HTMLButtonElement>(null);
  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <Box
      as="header"
      display="flex"
      alignItems="center"
      gap="5"
      bg={bg}
      px="5"
      py="3"
      shadow="base"
    >
      <Heading fontFamily="mono" letterSpacing="0.125rem" fontWeight="400" textTransform="uppercase" size="md" as="h1" flex="1" color={linkColor}>
        <Link to="/">WhalesAI.org</Link>
      </Heading>
      <ToggleColor />
      {!isMediumDown ? (
        <Box as="nav">
          <NavMenuList isAuthenticated={isAuthenticated} handleLogout={handleLogout} isMediumDown={isMediumDown} />
        </Box>
      ) : (
        <>
          <IconButton ref={btnRef} size="sm" variant="primary-text" onClick={onOpen} aria-label="Open mobile menu" icon={<CollecticonHamburgerMenu />} />
          <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader as="h1" letterSpacing="0.125rem" fontWeight="400" fontFamily="mono" textTransform="uppercase" color={linkColor}>
                WhalesAI.org
              </DrawerHeader>

              <DrawerBody as="nav">
                <NavMenuList isAuthenticated={isAuthenticated} handleLogout={handleLogout} isMediumDown={isMediumDown} />
              </DrawerBody>
            </DrawerContent>
          </Drawer>
        </>
      )}
    </Box>
  );
}

export default GlobalPageHeader;
